import { Box, Button, ButtonGroup, Dialog, Fab, Paper, TableContainer } from "@mui/material"
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { Localized, useLocalization } from "@fluent/react"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { selectSettings } from "../../settings/settingsSlice"
import { unwrapResult } from "@reduxjs/toolkit"
import { LoadingContainer } from "../../../app/LoadingContainer"
import AddIcon from '@mui/icons-material/Add'
import produce from "immer"
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"
import { loadSystemRoleResponsibilities, SystemRoleResponsibility } from "./systemRoleReponsibilitiesApi"
import { ErrorContainer } from "../../../app/ErrorContainer"
import { CreateEditSystemRoleReponsibilitiesDialog } from "./CreateEditSystemRoleReponsibilitiesDialog"
import { DeleteSystemRoleResponsibilityDialog } from "./DeleteSystemRoleResponsibilityDialog"
import { TeamMemberRoleLabel } from "../../documents/components/teamMembers/TeamMemberRoleLabel"
import { DocumentTeamMemberRole } from "../../documents/components/teamMemberApi"

type DialogState =
    | { type: "none" }
    | { type: "createEdit", systemRoleResponsibility: SystemRoleResponsibility | undefined }
    | { type: "delete", systemRoleResponsibilityId: string }

export const SystemRoleReponsibilities = () => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const dispatch = useAppDispatch();
    const [systemRoleResponsibilities, setSystemRoleResponsibilities] = useState<SystemRoleResponsibility[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const [dialog, setDialog] = useState<DialogState>({ type: "none" });

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadSystemRoleResponsibilities())
            .then(unwrapResult)
            .then(setSystemRoleResponsibilities)
            .catch((error) => { console.log(error); setIsError(true); setError(error); })
            .finally(() => setIsLoading(false));
    }, [dispatch])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const DialogContentDispatcher = ({ state, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
        const onSuccess = useCallback((systemRoleResponsibility: SystemRoleResponsibility | string) => {
            setter({ type: "none" })
            setSystemRoleResponsibilities(
                produce((draft) => {
                    if (draft !== undefined) {
                        if (typeof systemRoleResponsibility === "string") {
                            const existingIndex = draft.findIndex((group) => group.id === systemRoleResponsibility);
                            draft.splice(existingIndex, 1);
                        } else {
                            const existingIndex = draft.findIndex((group) => group.id === systemRoleResponsibility.id);

                            if (existingIndex !== -1) {
                                draft[existingIndex] = systemRoleResponsibility;
                            } else {
                                draft.push(systemRoleResponsibility);
                            }
                        }
                    }
                })
            )
        }, [setter, dispatch])

        const onCancel = useCallback(() => {
            setter({ type: "none" })
        }, [setter])

        const onDeleteSuccess = useCallback((id: string) => {
            setDialog({ type: "none" })
            setSystemRoleResponsibilities(
                produce((draft) => {
                    if (draft !== undefined) {
                        const existingIndex = draft.findIndex((group) => group.id === id);
                        if (existingIndex !== -1) {
                            draft.splice(existingIndex, 1);
                        }
                    }
                })
            )
        }, [])

        switch (state.type) {
            case "none":
                return null

            case "createEdit":
                return <CreateEditSystemRoleReponsibilitiesDialog
                    mode={state.systemRoleResponsibility ? "edit" : "create"}
                    systemRoleResponsibility={state.systemRoleResponsibility}
                    onSaveSuccess={onSuccess}
                    onCancel={onCancel}
                />
            case "delete":
                return <DeleteSystemRoleResponsibilityDialog
                    systemRoleResponsibilityId={state.systemRoleResponsibilityId}
                    onSaveSuccess={onDeleteSuccess}
                    onCancel={onCancel}
                />
        }
    }

    const handleAddSystemRoleResponsibilityClick = useCallback(() => {
        setDialog({
            type: "createEdit",
            systemRoleResponsibility: undefined
        })
    }, [setDialog])

    const handleEditSystemRoleResponsibilityClick = useCallback((systemRoleResponsibility: SystemRoleResponsibility) => {
        setDialog({
            type: "createEdit",
            systemRoleResponsibility
        })
    }, [setDialog])

    const handleDeleteSystemRoleResponsibilityClick = useCallback((id: string) => {
        setDialog({
            type: "delete",
            systemRoleResponsibilityId: id
        })
    }, [setDialog])

    const columns: GridColDef<SystemRoleResponsibility, any, any>[] = [
        {
            field: 'roleCode',
            headerName: l10n.getString("document-user-role", null, "_roleCode"),
            width: 300,
            renderCell: (params) => (
                <TeamMemberRoleLabel role={params.value as DocumentTeamMemberRole} />
            ),
        },
        {
            field: 'responsibilityPl',
            headerName: l10n.getString("role-responsibility-pl", null, "_responsibilityPl"),
            flex: 1,
        },
        {
            field: 'responsibilityEn',
            headerName: l10n.getString("role-responsibility-en", null, "_responsibilityEn"),
            flex: 1,
        },

        {
            field: 'actions',
            type: 'actions',
            width: 250,
            renderCell: (params) => {
                return (
                    <ButtonGroup size="small">
                        <Button
                            style={{ width: 100 }}
                            color="secondary"
                            variant="outlined"
                            onClick={() => handleEditSystemRoleResponsibilityClick(params.row)}
                        >
                            <Localized id="edit">
                                <span>_Edytuj</span>
                            </Localized>
                        </Button>
                        <Button
                            style={{ width: 100 }}
                            color="error"
                            variant="outlined"
                            onClick={() => handleDeleteSystemRoleResponsibilityClick(params.row.id)}
                        >
                            <Localized id="delete">
                                <span>_Usuń</span>
                            </Localized>
                        </Button>
                    </ButtonGroup>
                )
            }
        }
    ]

    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    return (
        <>
            <TableContainer component={Paper}>
                <DataGrid localeText={getCustomLocaleText()}
                    style={{ minHeight: 100, height: 'calc(100vh - 275px)' }}
                    rows={systemRoleResponsibilities}
                    columns={columns}
                    initialState={{
                        sorting: { sortModel: [{ field: 'eventDate', sort: 'desc' }] }
                    }}
                    slots={{ toolbar: QuickSearchToolbar }}
                />
                <Box sx={{
                    display: 'flex',
                }}>
                    <Fab sx={{
                        margin: 1,
                    }}
                        onClick={() => handleAddSystemRoleResponsibilityClick()}
                        color="secondary"
                        size="small"
                        aria-label="add"
                    >
                        <AddIcon />
                    </Fab>
                </Box>
            </TableContainer>
            <Dialog open={dialog.type !== "none"} fullWidth maxWidth="sm" keepMounted={false}>
                <DialogContentDispatcher state={dialog} setter={setDialog} />
            </Dialog>
        </>
    )
}
